import React, { useCallback, useEffect, useState } from "react";
import { useStripeAppearance } from "@/lib/stripeAppearance";
import { Elements, AddressElement } from "@stripe/react-stripe-js";
import { Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { getStripe } from "@/lib/stripe";
import { platformClient } from "@/lib/musicApi";
import { CoinQuoteBox } from "@/components/wallet/CoinQuoteBox";
import i18n from "@/i18n";

const errKey = (reason) => ({ tax_unavailable: "coins.taxUnavailable", address_required: "coins.addressRequired",
  amount_changed: "coins.amountChanged", surface_banned: "checkout.errBanned" }[reason] || "shop.errCheckout");

// Coin cart: billing address (digital-only) -> server quote incl. tax -> "Pay X coins".
// Nothing is spent here; onAccept hands the agreed amount to the spending step.
export const CoinCartQuote = ({ items, toApiItem, shipping, hasPhysical, notice, onAccept, onCard }) => {
  const stripeAppearance = useStripeAppearance();
  const { t } = useTranslation();
  const [billing, setBilling] = useState(null);
  const [quote, setQuote] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const addr = hasPhysical ? shipping : billing;

  const fetchQuote = useCallback(async () => {
    setBusy(true); setQuote(null);
    try {
      const { data } = await platformClient.post("/shop/checkout/cart", {
        items: items.map(toApiItem), shipping_address: shipping || undefined, billing_address: hasPhysical ? undefined : billing,
        use_coins: true, quote_only: true, lang: (i18n?.language || "en").split("-")[0] }, { restrictedInline: true });
      if (data.coin_error) { setError(errKey(data.coin_error)); return; }
      setQuote({ subtotal_cents: data.digital_subtotal_cents, tax_cents: data.digital_tax_cents,
        digital_total_cents: data.digital_total_cents, merch_total_cents: data.merch_total_cents });
    } catch (e) {
      setError(errKey(e?.response?.data?.detail?.reason));
    } finally { setBusy(false); }
  }, [items, toApiItem, shipping, billing, hasPhysical]);

  useEffect(() => { if (addr) fetchQuote(); }, [addr]); // eslint-disable-line react-hooks/exhaustive-deps

  const taxDown = error === "coins.taxUnavailable" || (!error && notice === "coins.taxUnavailable");
  return (
    <div className="space-y-3" data-testid="shop-coin-quote-step">
      {!hasPhysical && (
        <div data-testid="shop-coin-billing">
          <label className="text-xs mb-1 block" style={{ color: "var(--text-secondary)" }}>{t("shop.billingAddress")}</label>
          <Elements stripe={getStripe()} options={{ appearance: stripeAppearance }}>
            <AddressElement options={{ mode: "billing" }} onChange={(e) => { setError(""); setBilling(e.complete ? e.value.address : null); if (!e.complete) setQuote(null); }} />
          </Elements>
        </div>
      )}
      {notice && !error && <p className="text-sm" style={{ color: "var(--text-primary)" }} data-testid="shop-coin-quote-notice">{t(notice)}</p>}
      {error && <p className="text-sm" style={{ color: taxDown ? "var(--text-primary)" : "#ef4444" }} data-testid="shop-coin-quote-error">{t(error)}</p>}
      {busy && <p className="text-sm flex items-center gap-2" style={{ color: "var(--text-secondary)" }} data-testid="shop-coin-quote-loading"><Loader2 size={16} className="animate-spin" /> {t("coins.quoteLoading")}</p>}
      {quote && !error && (
        <CoinQuoteBox quote={quote} merchCents={quote.merch_total_cents} testid="shop-coin-quote"
          onPay={() => onAccept({ billing: hasPhysical ? null : billing, expected: quote.digital_total_cents })} />
      )}
      {error && addr && (
        <button type="button" onClick={() => { setError(""); fetchQuote(); }} data-testid="shop-coin-quote-retry" className="w-full py-2 text-xs underline" style={{ color: "var(--text-secondary)" }}>{t("coins.quoteRetry")}</button>
      )}
      <button type="button" onClick={onCard} data-testid="shop-coin-quote-card" className="w-full py-2.5 rounded-full text-sm font-semibold" style={{ background: taxDown ? "var(--accent-primary)" : "var(--bg-subtle)", color: taxDown ? "#fff" : "var(--text-primary)", border: "1px solid var(--border-color)" }}>{t("cart.coins.payByCard")}</button>
    </div>
  );
};

export default CoinCartQuote;
